import { axiosInstance } from "./ServiceConstants";
import axios, { CancelToken } from "axios";
import { IEvent, IEventCore, INewEvent, IOrganizerEvent, IRound } from "../models/IEvent";
import IRider from "../models/IRider";
import { AxiosResponse } from "axios";
import IParsingRidersFileResult from "../models/IParsingRidersFileResult";
import { AuthService } from "./Auth/AuthService";
import { IRidersDistribution } from "../models/IRidersDistribution";

class EventService {
  async cancelEvent(raceId: string): Promise<void> {
    await axiosInstance.delete<IOrganizerEvent>(`api/events/${raceId}`);
  }

  async recalculateEventLaps(raceId: string): Promise<void> {
    await axiosInstance.post(`api/Events/${raceId}/recalculate-laps`);
  }
  async stopEvent(raceId: string): Promise<IOrganizerEvent> {
    const result = await axiosInstance.post<IOrganizerEvent>(`api/events/${raceId}/stop`);
    return result.data;
  }
  async startEvent(raceId: string): Promise<IOrganizerEvent> {
    const result = await axiosInstance.post<IOrganizerEvent>(`api/events/${raceId}/start`);
    return result.data;
  }

  async importParticipants(
    raceId: string,
    files: File[]
  ): Promise<IParsingRidersFileResult> {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files" + i, files[i], files[i].name);
    }
    const url = `api/events/${raceId}/participants?save=true`;
    const result = await axiosInstance.put<IParsingRidersFileResult>(
      url,
      formData
    );
    return result.data;
  }

  merge(
    evt: IOrganizerEvent,
    importResult: IParsingRidersFileResult
  ): IOrganizerEvent {
    const importedRiders = Array.isArray(importResult.riders)
      ? importResult.riders
      : [];

    const merged: IOrganizerEvent = {
      ...evt,
      pilots: importedRiders,
      upsertPilots: [],
      deletedPilots: [],
    };

    if (Array.isArray(importResult.groups)) {
      merged.groups = importResult.groups;
    }

    return merged;
  }

  async uploadBackupFiles(raceId: string, readerId: string, files: FileList) {
    const formData = new FormData();

    // Update the formData object
    formData.append("raceId", raceId);
    formData.append("readerId", readerId);
    // formData.append("files", files);
    for (let i = 0; i < files.length; i++) {
      formData.append("files" + i, files[i], files[i].name);
    }
    await axiosInstance.post("api/tags/import", formData);
  }
  async downloadFile(resourceUrl: string, fileName: string) {
    const response = await axiosInstance.get(resourceUrl, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/octet-stream" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
  }
  async downloadEventSummary(raceId: string) {
    await this.downloadFile(`api/events/${raceId}/summary`, `${raceId}.xlsx`);
  }
  async exportParticipants(raceId: string) {
    await this.downloadFile(`api/events/${raceId}/participants`, `${raceId}.xlsx`);
  }

  async downloadErrors(raceId: string, errorsId: string) {
    this.downloadFile(
      `api/events/${raceId}/participants/errors?errorsId=${errorsId}`,
      errorsId
    );
  }

  async getEvents(from?: Date, to?: Date, cancelToken?: CancelToken): Promise<IEvent[]> {
    try {
      let eventsResponse = await axiosInstance.get<IEvent[]>("api/events", { cancelToken });
      return eventsResponse.data;
    } catch (e) {
      if (axios.isCancel(e)) {
        throw e;
      }
      console.error(e);
      return [];
    }
  }

  async getOrganizerEvents(from?: Date, to?: Date, cancelToken?: CancelToken): Promise<IEvent[]> {
    try {
      const params = new URLSearchParams();
      if (from) {
        params.append("from", from.toISOString());
      }
      if (to) {
        params.append("to", to.toISOString());
      }
      const url =
        params.toString().length > 0
          ? `api/events/organizer?${params.toString()}`
          : "api/events/organizer";
      let eventsResponse = await axiosInstance.get<IEvent[]>(url, { cancelToken });
      return eventsResponse.data;
    } catch (e) {
      if (axios.isCancel(e)) {
        throw e;
      }
      console.error(e);
      return [];
    }
  }

  async getEvent(eventId: string, cancelToken?: CancelToken): Promise<IEventCore | null> {
    try {
      let eventsResponse = await axiosInstance.get<IEventCore>(
        `api/events/${eventId}`,
        { cancelToken }
      );
      return eventsResponse.data;
    } catch (e) {
      if (axios.isCancel(e)) {
        throw e;
      }
      console.error(e);
      return null;
    }
  }

  async getEventOrganizer(eventId: string, cancelToken?: CancelToken): Promise<IOrganizerEvent | null> {
    try {
      const eventResponse = await axiosInstance.get<IOrganizerEvent>(
        `api/events/${eventId}/organizer`,
        { cancelToken }
      );
      return eventResponse.data;
    } catch (e) {
      if (axios.isCancel(e)) {
        throw e;
      }
      return null;
    }
  }

  async newEvent(event: INewEvent): Promise<IOrganizerEvent> {
    if (!event.organizerId) {
      const authService = await AuthService.theInstance();
      event.organizerId = authService.getUserId() ?? '';
    }
    // there is no race id, then is a new event
    const eventResponse = await axiosInstance.post<IOrganizerEvent>(
      "api/events",
      event
    );
    return eventResponse.data;
  }

  async saveEvent(event: IOrganizerEvent): Promise<IOrganizerEvent> {
    const selectedReaderIds = Array.from(
      new Set(
        (event.checkpoints ?? [])
          .map((checkpoint) => checkpoint.readerId)
          .filter((readerId) => readerId.length > 0)
      )
    );
    const payload: IOrganizerEvent = {
      ...event,
      readers: selectedReaderIds,
    };

    let eventResponse: AxiosResponse<IOrganizerEvent>;
    if (event.raceId) {
      // the race id already exists, so it is an existing event to be updated
      // only changed riders needs to be sent. clear riders to reduce the payload

      payload.pilots = [];

      eventResponse = await axiosInstance.put<IOrganizerEvent>(
        "api/events",
        payload
      );
    } else {
      // there is no race id, then is a new event
      eventResponse = await axiosInstance.post<IOrganizerEvent>(
        "api/events",
        payload
      );
    }
    return eventResponse.data;
  }

  async getRiders(eventId: string, unmasked?: boolean, cancelToken?: CancelToken): Promise<IRider[]> {
    const url = unmasked
      ? `api/events/${eventId}/riders?unmasked=true`
      : `api/events/${eventId}/riders`;
    let ridersResponse = await axiosInstance.get<IRider[]>(url, { cancelToken });
    return ridersResponse.data;
  }

  async getRidersDistribution(raceId: string, numberOfGroups: number, cancelToken?: CancelToken): Promise<IRidersDistribution | null> {
    try {
      const result = await axiosInstance.get<IRidersDistribution>(
        `api/Events/${raceId}/riders/distribution?numberOfGroups=${numberOfGroups}`,
        { cancelToken }
      );
      return result.data;
    } catch (e) {
      if (axios.isCancel(e)) throw e;
      console.error(e);
      return null;
    }
  }

  async getRounds(raceId: string, cancelToken?: CancelToken): Promise<IRound[]> {
    try {
      const result = await axiosInstance.get<IRound[]>(
        `api/Events/${raceId}/rounds`,
        { cancelToken }
      );
      return result.data;
    } catch (e) {
      if (axios.isCancel(e)) throw e;
      console.error(e);
      return [];
    }
  }

  async startRound(raceId: string): Promise<IRound> {
    const result = await axiosInstance.post<IRound>(
      `api/Events/${raceId}/rounds/start`
    );
    return result.data;
  }

  async endRound(raceId: string): Promise<IRound> {
    const result = await axiosInstance.post<IRound>(
      `api/Events/${raceId}/rounds/end`
    );
    return result.data;
  }

  async updateRound(raceId: string, roundId: string, round: IRound): Promise<IRound> {
    const result = await axiosInstance.put<IRound>(
      `api/Events/${raceId}/rounds/${roundId}`,
      round
    );
    return result.data;
  }

  async deleteRound(raceId: string, roundId: string): Promise<void> {
    await axiosInstance.delete(`api/Events/${raceId}/rounds/${roundId}`);
  }

  async getRidersByRound(raceId: string, roundId: string, cancelToken?: CancelToken): Promise<IRider[]> {
    try {
      const result = await axiosInstance.get<IRider[]>(
        `api/Events/${raceId}/rounds/${roundId}/riders`,
        { cancelToken }
      );
      return result.data;
    } catch (e) {
      if (axios.isCancel(e)) throw e;
      console.error(e);
      return [];
    }
  }
}

const EventServiceInstance = new EventService();

export default EventServiceInstance;
